import React, { useEffect, useState, useMemo } from "react";
import { useConfig } from "../lib/ConfigContext";
import { Slider } from "../components/ui/slider";
import { Button } from "../components/ui/button";
import { Filter, X, Building, Home, Building2, Layers, Sparkles, Hammer, Store, Trees, MapPin, User, Briefcase, HardHat } from "lucide-react";
import { formatINR } from "../lib/api";

const TYPE_ICONS = {
  "Builder Floor": Layers,
  "Independent House": Home,
  "Building": Building2,
  "New Construction": Sparkles,
  "Resale Property": Hammer,
  "Shop": Store,
  "Office": Briefcase,
  "Plot": Trees,
};

// v2.85 Phase B — Per-intent budget configuration. Spec mandates that
// Rent / Lease use realistic monthly-rupee ladders, NOT the same crore-
// scale buckets that apply to Sale. The frontend reads `filters.
// listing_intent` and swaps both the LABEL and the dropdown options
// without touching backend storage (price is stored in plain rupees and
// the rent listings already carry monthly amount in the `price` field
// per buildPayload in UnifiedSubmissionForm.jsx).
const INTENT_BUDGET = {
  sale: {
    label: "Sale Budget",
    placeholder: "Pick a range",
    presets: [
      { label: "₹25L", value: 2500000 },
      { label: "₹50L", value: 5000000 },
      { label: "₹1Cr", value: 10000000 },
      { label: "₹3Cr", value: 30000000 },
      { label: "₹10Cr", value: 100000000 },
      { label: "₹30Cr", value: 300000000 },
    ],
    options: [
      { v: 500000, l: "₹5 Lakh" },
      { v: 1000000, l: "₹10 Lakh" },
      { v: 1500000, l: "₹15 Lakh" },
      { v: 2000000, l: "₹20 Lakh" },
      { v: 2500000, l: "₹25 Lakh" },
      { v: 3000000, l: "₹30 Lakh" },
      { v: 3500000, l: "₹35 Lakh" },
      { v: 4000000, l: "₹40 Lakh" },
      { v: 5000000, l: "₹50 Lakh" },
      { v: 7500000, l: "₹75 Lakh" },
      { v: 10000000, l: "₹1 Crore" },
      { v: 15000000, l: "₹1.5 Crore" },
      { v: 20000000, l: "₹2 Crore" },
      { v: 30000000, l: "₹3 Crore" },
      { v: 50000000, l: "₹5 Crore" },
      { v: 100000000, l: "₹10 Crore" },
      { v: 200000000, l: "₹20 Crore" },
      { v: 300000000, l: "₹30 Crore" },
    ],
  },
  rent: {
    label: "Monthly Rent",
    placeholder: "Pick a monthly range",
    presets: [
      { label: "₹10k", value: 10000 },
      { label: "₹20k", value: 20000 },
      { label: "₹30k", value: 30000 },
      { label: "₹50k", value: 50000 },
      { label: "₹1L", value: 100000 },
    ],
    options: [
      { v: 5000, l: "₹5,000" },
      { v: 10000, l: "₹10,000" },
      { v: 15000, l: "₹15,000" },
      { v: 20000, l: "₹20,000" },
      { v: 25000, l: "₹25,000" },
      { v: 30000, l: "₹30,000" },
      { v: 40000, l: "₹40,000" },
      { v: 50000, l: "₹50,000" },
      { v: 75000, l: "₹75,000" },
      { v: 100000, l: "₹1 Lakh" },
      { v: 200000, l: "₹2 Lakh" },
      { v: 500000, l: "₹5 Lakh" },
    ],
  },
  lease: {
    label: "Lease Amount / Deposit",
    placeholder: "Pick a range",
    presets: [
      { label: "₹25k", value: 25000 },
      { label: "₹50k", value: 50000 },
      { label: "₹1L", value: 100000 },
      { label: "₹5L", value: 500000 },
    ],
    options: [
      { v: 10000, l: "₹10,000" },
      { v: 25000, l: "₹25,000" },
      { v: 50000, l: "₹50,000" },
      { v: 100000, l: "₹1 Lakh" },
      { v: 200000, l: "₹2 Lakh" },
      { v: 500000, l: "₹5 Lakh" },
      { v: 1000000, l: "₹10 Lakh" },
      { v: 5000000, l: "₹50 Lakh" },
    ],
  },
};

// v2.85 Phase B — Posted-By chip definitions. Each chip maps the public
// label to the canonical `submitted_by_role` value the backend stores.
// "Owner" → role "client" (kept user-friendly publicly).
const POSTED_BY_OPTIONS = [
  { key: "owner",   label: "Owner",   Icon: User },
  { key: "dealer",  label: "Dealer",  Icon: Briefcase },
  { key: "builder", label: "Builder", Icon: HardHat },
];

export default function FilterPanel({ filters, setFilters, onReset }) {
  // SSOT — read filters + locations from ConfigContext (one /api/config call)
  const { filters: config = [], locations = [] } = useConfig();

  const byKey = useMemo(() => Object.fromEntries(config.map((c) => [c.key, c])), [config]);
  const update = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    // v2.71.67 — behavioural signal for auto-leads (SmartEnquiryPopup retired).
    try { window.dispatchEvent(new CustomEvent("he:filter-use", { detail: { key } })); } catch { /* ignore */ }
  };

  const PRICE_MIN = byKey.price?.min ?? 500000;
  const PRICE_MAX = byKey.price?.max ?? 300000000;
  const PRICE_STEP = byKey.price?.step ?? 100000;
  const METRO_MIN = byKey.metro_distance?.min ?? 0;
  const METRO_MAX = byKey.metro_distance?.max ?? 3000;
  const METRO_STEP = byKey.metro_distance?.step ?? 100;

  // v2.85 Phase B — Resolve current intent (default "sale" / Buy) so the
  // budget label, options, and presets adapt to Buy / Rent / Lease. This
  // is the single source of truth — every budget render below reads from
  // here. Filters.priceRange continues to live in plain ₹ (rupees), so
  // the backend doesn't need to special-case anything.
  const intent = filters.listing_intent || "sale";
  const intentBudget = INTENT_BUDGET[intent] || INTENT_BUDGET.sale;
  // v2.85 Phase B (iter_92 fix) — Admin's `byKey.price.min/max` are
  // SALE-scoped (₹5 L → ₹30 Cr). Applying those bounds to the Rent /
  // Lease ladders strips the monthly-rupee options (₹5k → ₹2L for rent,
  // ₹10k → ₹50L for lease), collapsing the dropdown to a single value.
  // For non-sale intents we use the ladder's own min/max as bounds and
  // skip the admin clamp entirely.
  const isSale = intent === "sale";
  const ladderMin = intentBudget.options[0]?.v ?? PRICE_MIN;
  const ladderMax = intentBudget.options[intentBudget.options.length - 1]?.v ?? PRICE_MAX;
  const EFFECTIVE_MIN = isSale ? PRICE_MIN : ladderMin;
  const EFFECTIVE_MAX = isSale ? PRICE_MAX : ladderMax;

  // v2.85 Phase B (iter_94 follow-up) — When the listing intent changes
  // OR on first mount, renormalise `priceRange` so each value (a) sits
  // within [EFFECTIVE_MIN, EFFECTIVE_MAX] AND (b) maps to an actual
  // <option> on the new intent's ladder. Otherwise a value like 20000
  // carried from Rent into Lease would silently fall to the first
  // option in the select (cosmetic drift even though state is correct).
  useEffect(() => {
    const min = filters.priceRange?.[0];
    const max = filters.priceRange?.[1];
    const ladderValues = intentBudget.options.map((o) => o.v);
    const snapToLadder = (v, fallback) => {
      if (v == null) return fallback;
      if (ladderValues.includes(v)) return v;
      // Pick the ladder value closest to `v` (preferring downward for min,
      // upward for max — but plain "closest" is acceptable for both).
      return ladderValues.reduce((best, candidate) =>
        Math.abs(candidate - v) < Math.abs(best - v) ? candidate : best,
        ladderValues[0],
      );
    };
    const outOfRange =
      min == null || max == null ||
      min < EFFECTIVE_MIN || min > EFFECTIVE_MAX ||
      max < EFFECTIVE_MIN || max > EFFECTIVE_MAX;
    const offLadder =
      !outOfRange && (!ladderValues.includes(min) || !ladderValues.includes(max));
    if (outOfRange) {
      update("priceRange", [EFFECTIVE_MIN, EFFECTIVE_MAX]);
    } else if (offLadder) {
      update("priceRange", [snapToLadder(min, EFFECTIVE_MIN), snapToLadder(max, EFFECTIVE_MAX)]);
    }
  }, [EFFECTIVE_MIN, EFFECTIVE_MAX]);

  // v2.36 — Quick preset chips for the Budget range. Common ticket sizes for
  // Delhi/NCR. Chip is hidden if the value falls outside the EFFECTIVE bounds.
  // v2.85 Phase B — Presets now scoped to current intent (Rent uses
  // monthly-rupee shortcuts, Lease uses lease-amount shortcuts).
  const PRICE_PRESETS = intentBudget.presets.filter(
    (p) => p.value >= EFFECTIVE_MIN && p.value <= EFFECTIVE_MAX,
  );

  // v2.38 — Budget dropdown options (premium MagicBricks-style). Each option is
  // a labeled rupee tier the user can select. Filtered to EFFECTIVE bounds.
  // v2.85 Phase B — Per-intent budget ladder (no admin clamp for rent/lease).
  const BUDGET_OPTIONS = intentBudget.options.filter(
    (o) => o.v >= EFFECTIVE_MIN && o.v <= EFFECTIVE_MAX,
  );
  // v2.85 Phase B (iter_93 fix) — Also clamp `filters.priceRange` to the
  // EFFECTIVE per-intent bounds when computing min/max <option> lists.
  // Without this, the Dashboard's sale-scoped default priceRange=[500000,
  // 300000000] bled into the rent / lease Max selects and collapsed them.
  const _clamp = (v) => Math.max(EFFECTIVE_MIN, Math.min(EFFECTIVE_MAX, v));
  const effectiveMin = _clamp(filters.priceRange?.[0] ?? EFFECTIVE_MIN);
  const effectiveMax = _clamp(filters.priceRange?.[1] ?? EFFECTIVE_MAX);
  const minOptions = BUDGET_OPTIONS.filter((o) => o.v <= effectiveMax);
  const maxOptions = BUDGET_OPTIONS.filter((o) => o.v >= effectiveMin);

  const isActive = (k) => byKey[k] && byKey[k].status === "active";

  // v2.26 — memoize custom filters list (was inline .filter every render)
  const customFilters = useMemo(
    () => config.filter((f) => f.is_custom && f.status === "active"),
    [config]
  );

  const subAreasFor = useMemo(() => {
    if (!filters.main_area) return [];
    const m = locations.find((x) => x.name === filters.main_area);
    return m?.sub_areas || [];
  }, [locations, filters.main_area]);

  const Chip = ({ active, onClick, children, testid }) => (
    <button
      onClick={onClick}
      className={`text-[11px] font-medium px-3 py-1.5 border rounded-full transition-all inline-flex items-center gap-1.5 ${
        active ? "bg-[#09090B] text-white border-[#09090B] shadow-sm"
               : "bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#09090B] hover:text-[#09090B]"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );

  const TagChip = ({ active, onClick, children, testid }) => (
    <button
      onClick={onClick}
      className={`text-[11px] font-medium px-3 py-1.5 border rounded-full transition-all ${
        active ? "bg-[#0055FF] text-white border-[#0055FF] shadow-sm"
               : "bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#0055FF] hover:text-[#0055FF]"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );

  const activeCount = [
    filters.listing_type, filters.property_type, filters.bhk, filters.main_area, filters.sub_area, filters.tag,
    filters.posted_by,
    (filters.priceRange?.[0] ?? PRICE_MIN) > PRICE_MIN || (filters.priceRange?.[1] ?? PRICE_MAX) < PRICE_MAX ? "price" : null,
    (filters.maxMetro ?? METRO_MAX) < METRO_MAX ? "metro" : null,
  ].filter(Boolean).length;

  return (
    <aside className="bg-white border border-[#E4E4E7] rounded-sm p-5 sticky top-20 space-y-5 max-h-[calc(100vh-100px)] overflow-y-auto" data-testid="filter-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-[15px] tracking-tight flex items-center gap-2">
          <Filter size={14} /> {byKey.property_type?.name ? "Refine" : "Refine"}
          {activeCount > 0 && (
            <span className="text-[10px] uppercase tracking-wider font-semibold bg-[#0055FF] text-white px-1.5 py-0.5 rounded-full">
              {activeCount}
            </span>
          )}
        </h3>
        <Button variant="ghost" size="sm" onClick={onReset} className="text-[11px] h-7 px-2 rounded-sm" data-testid="reset-filters-btn">
          <X size={12} className="mr-1" /> Reset
        </Button>
      </div>

      {/* Property Condition (was "Inventory Type" / "Listing Type" before v2.87) —
          rendered first when active.
          v2.85 Phase B — Construction Status is a Buy-only concept; suppress
          when the active intent is Rent or Lease per user spec.
          v2.87 Phase 1 — Renamed to "Property Condition" to match the v2.86
          listing-form label. Backend key `listing_type` unchanged. */}
      {isActive("listing_type") && intent === "sale" && (byKey.listing_type.options || []).length > 0 && (
        <div data-testid="filter-listing_type">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] block mb-2">
            Property Condition
            <span className="ml-1 font-normal text-[#A1A1AA] normal-case tracking-normal">(multi-select)</span>
          </label>
          <div className="flex flex-wrap gap-1.5">
            {byKey.listing_type.options.map((k) => {
              const Icon = k === "New" ? Sparkles : Hammer;
              const ltArr = String(filters.listing_type || "").split(",").map((x) => x.trim()).filter(Boolean);
              const isOn = ltArr.includes(k);
              return (
                <Chip
                  key={k}
                  active={isOn}
                  onClick={() => {
                    const next = isOn ? ltArr.filter((x) => x !== k) : [...ltArr, k];
                    update("listing_type", next.join(","));
                  }}
                  testid={`listing-type-chip-${k}`}
                >
                  <Icon size={11} /> {k}
                </Chip>
              );
            })}
          </div>
        </div>
      )}

      {/* Property Type — v2.87.x Phase C multi-select via CSV toggle */}
      {isActive("property_type") && (byKey.property_type.options || []).length > 0 && (
        <div data-testid="filter-property_type">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] block mb-2">
            {byKey.property_type.name}
            <span className="ml-1 font-normal text-[#A1A1AA] normal-case tracking-normal">(multi-select)</span>
          </label>
          <div className="flex flex-wrap gap-1.5">
            {byKey.property_type.options.map((key) => {
              const Icon = TYPE_ICONS[key] || Building;
              const typeArr = String(filters.property_type || "").split(",").map((x) => x.trim()).filter(Boolean);
              const isOn = typeArr.includes(key);
              return (
                <Chip
                  key={key}
                  active={isOn}
                  onClick={() => {
                    const next = isOn ? typeArr.filter((x) => x !== key) : [...typeArr, key];
                    update("property_type", next.join(","));
                  }}
                  testid={`type-chip-${key.replace(/ /g, "-")}`}
                >
                  <Icon size={11} /> {key}
                </Chip>
              );
            })}
          </div>
        </div>
      )}

      {/* Budget — premium dropdown selectors + chips (v2.38)
          v2.85 Phase B — Label + dropdown options + presets all adapt to
          the active intent ("Sale Budget" / "Monthly Rent" / "Lease Amount").
          The underlying URL key `priceRange` is unchanged, so back-nav and
          shared links continue to work for all intents. */}
      {isActive("price") && (
        <div data-testid="filter-price" className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-[10px] uppercase tracking-[0.16em] font-bold text-zinc-700" data-testid="filter-budget-label">{intentBudget.label}</label>
            <span className="text-[11px] font-mono-data text-[#0055FF] font-bold" data-testid="price-range-label">
              {formatINR(filters.priceRange?.[0] ?? EFFECTIVE_MIN)} – {formatINR(filters.priceRange?.[1] ?? EFFECTIVE_MAX)}
            </span>
          </div>

          {/* Min / Max dropdowns — stack vertically on narrow viewports for thumb-friendly tap */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <BudgetSelect
              label={intent === "rent" ? "Min Monthly Rent" : intent === "lease" ? "Min Lease" : "Min Budget"}
              value={filters.priceRange?.[0] ?? EFFECTIVE_MIN}
              options={minOptions}
              onChange={(v) =>
                update("priceRange", [v, Math.max(v, filters.priceRange?.[1] ?? EFFECTIVE_MAX)])
              }
              testid="price-min-select"
            />
            <BudgetSelect
              label={intent === "rent" ? "Max Monthly Rent" : intent === "lease" ? "Max Lease" : "Max Budget"}
              value={filters.priceRange?.[1] ?? EFFECTIVE_MAX}
              options={maxOptions}
              onChange={(v) =>
                update("priceRange", [Math.min(filters.priceRange?.[0] ?? EFFECTIVE_MIN, v), v])
              }
              testid="price-max-select"
            />
          </div>

          {/* Premium quick presets (Max budget shortcuts) */}
          {PRICE_PRESETS.length > 0 && (
            <div data-testid="price-presets">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-zinc-500 mb-2">Quick presets — Max</div>
              <div className="flex flex-wrap gap-1.5">
                {PRICE_PRESETS.map((p) => {
                  const active = (filters.priceRange?.[1] ?? PRICE_MAX) === p.value;
                  return (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() =>
                        update("priceRange", [Math.min(filters.priceRange?.[0] ?? EFFECTIVE_MIN, p.value), p.value])
                      }
                      className={`text-[12px] font-bold px-3.5 py-2 rounded-full transition-all min-h-[36px] ${
                        active
                          ? "btn-gradient text-white shadow-md shadow-blue-500/20"
                          : "bg-white text-zinc-700 border border-zinc-200 hover:border-[#0055FF] hover:text-[#0055FF] hover:-translate-y-0.5"
                      }`}
                      data-testid={`price-preset-${p.value}`}
                      aria-pressed={active}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* BHK — v2.87.x Phase C multi-select via CSV toggle */}
      {isActive("bhk") && (byKey.bhk.options || []).length > 0 && (
        <div data-testid="filter-bhk">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] block mb-2">
            {byKey.bhk.name}
            <span className="ml-1 font-normal text-[#A1A1AA] normal-case tracking-normal">(multi-select)</span>
          </label>
          <div className="flex flex-wrap gap-1.5">
            {byKey.bhk.options.map((b) => {
              const bhkArr = String(filters.bhk || "").split(",").map((x) => x.trim()).filter(Boolean);
              const isOn = bhkArr.includes(b);
              return (
                <Chip
                  key={b}
                  active={isOn}
                  onClick={() => {
                    const next = isOn ? bhkArr.filter((x) => x !== b) : [...bhkArr, b];
                    update("bhk", next.join(","));
                  }}
                  testid={`bhk-chip-${b.replace(/ /g, "-")}`}
                >
                  {b}
                </Chip>
              );
            })}
          </div>
        </div>
      )}

      {/* Hierarchical Location: Main Area → Sub Area */}
      {isActive("main_area") && (
        <div data-testid="filter-main_area">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] block mb-2 inline-flex items-center gap-1.5">
            <MapPin size={11} /> {byKey.main_area.name}
          </label>
          <select
            value={filters.main_area || ""}
            onChange={(e) => {
              const v = e.target.value;
              update("main_area", v);
              update("sub_area", ""); // reset sub when main changes
            }}
            className="w-full h-9 rounded-sm border border-[#E4E4E7] px-2 text-[13px] bg-white"
            data-testid="main-area-select"
          >
            <option value="">All main areas</option>
            {locations.map((m) => (
              <option key={m.id} value={m.name}>{m.name}</option>
            ))}
          </select>
          {isActive("sub_area") && filters.main_area && subAreasFor.length > 0 && (
            <div className="mt-2" data-testid="filter-sub_area">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#71717A] block mb-1">{byKey.sub_area?.name || "Sub Area"}</label>
              <select
                value={filters.sub_area || ""}
                onChange={(e) => update("sub_area", e.target.value)}
                className="w-full h-9 rounded-sm border border-[#E4E4E7] px-2 text-[13px] bg-white"
                data-testid="sub-area-select"
              >
                <option value="">All sub areas</option>
                {subAreasFor.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Metro distance */}
      {isActive("metro_distance") && (
        <div data-testid="filter-metro_distance">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] block mb-2">{byKey.metro_distance.name}</label>
          <div className="flex items-center gap-2">
            <Slider
              min={METRO_MIN} max={METRO_MAX} step={METRO_STEP}
              value={[filters.maxMetro ?? METRO_MAX]}
              onValueChange={(v) => update("maxMetro", v[0])}
              data-testid="metro-distance-slider"
            />
            <span className="text-[11px] font-mono-data text-[#09090B] w-16 text-right">
              {(filters.maxMetro ?? METRO_MAX) === METRO_MAX ? "Any" : `${filters.maxMetro}m`}
            </span>
          </div>
        </div>
      )}

      {/* Tags (other than "Verified" — now surfaced as a dedicated pill in FilterPills v2.87).
          v2.87 Phase 3b ext — Multi-select: each chip toggles a CSV value. */}
      {isActive("tag") && (byKey.tag.options || []).filter((t) => t !== "Verified").length > 0 && (
        <div data-testid="filter-tag">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] block mb-2">
            {byKey.tag.name}
            <span className="ml-1 font-normal text-[#A1A1AA] normal-case tracking-normal">(multi-select)</span>
          </label>
          <div className="flex flex-wrap gap-1.5">
            {byKey.tag.options.filter((t) => t !== "Verified").map((t) => {
              const tagArr = String(filters.tag || "").split(",").map((x) => x.trim()).filter(Boolean);
              const isOn = tagArr.includes(t);
              return (
                <TagChip
                  key={t}
                  active={isOn}
                  onClick={() => {
                    const next = isOn ? tagArr.filter((x) => x !== t) : [...tagArr, t];
                    update("tag", next.join(","));
                  }}
                  testid={`tag-chip-${t.replace(/ /g, "-")}`}
                >
                  {t}
                </TagChip>
              );
            })}
          </div>
        </div>
      )}

      {/* v2.85 Phase B — Posted By (Owner / Dealer / Builder). Always visible
          across all intents (Buy / Rent / Lease) per spec. Frontend key
          `posted_by` is forwarded to /api/properties/search where the
          backend resolves it to submitted_by_role. */}
      <div data-testid="filter-posted_by">
        <label className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] block mb-2">Posted By</label>
        <div className="flex flex-wrap gap-1.5">
          {POSTED_BY_OPTIONS.map(({ key, label, Icon }) => (
            <Chip
              key={key}
              active={filters.posted_by === key}
              onClick={() => update("posted_by", filters.posted_by === key ? "" : key)}
              testid={`posted-by-chip-${key}`}
            >
              <Icon size={11} /> {label}
            </Chip>
          ))}
        </div>
      </div>
      {/* Custom (admin-added) filters — dynamic.
          v2.87.x — Dropdown filters render as CSV multi-toggle chips to
          stay consistent with the outer FilterPills behavior. */}
      {customFilters.map((f) => {
          if (f.type === "dropdown" && (f.options || []).length > 0) {
            return (
              <div key={f.key} data-testid={`filter-${f.key}`}>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] block mb-2">
                  {f.name}
                  <span className="ml-1 font-normal text-[#A1A1AA] normal-case tracking-normal">(multi-select)</span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {f.options.map((o) => {
                    const arr = String(filters[f.key] || "").split(",").map((x) => x.trim()).filter(Boolean);
                    const isOn = arr.includes(o);
                    return (
                      <Chip
                        key={o}
                        active={isOn}
                        onClick={() => {
                          const next = isOn ? arr.filter((x) => x !== o) : [...arr, o];
                          update(f.key, next.join(","));
                        }}
                        testid={`custom-${f.key}-chip-${String(o).replace(/ /g, "-")}`}
                      >
                        {o}
                      </Chip>
                    );
                  })}
                </div>
              </div>
            );
          }
          if (f.type === "toggle") {
            const isOn = !!filters[f.key];
            return (
              <div key={f.key} data-testid={`filter-${f.key}`}>
                <button
                  onClick={() => update(f.key, !isOn)}
                  className={`w-full text-left px-3 py-2.5 border rounded-sm transition-all flex items-center justify-between ${
                    isOn ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white border-[#E4E4E7] hover:border-[#0055FF]"
                  }`}
                  data-testid={`custom-${f.key}-toggle`}
                >
                  <span className="text-[12px] font-semibold">{f.name}</span>
                  <span className="text-[10px] uppercase tracking-wider opacity-80">{isOn ? "ON" : "OFF"}</span>
                </button>
              </div>
            );
          }
          if (f.type === "slider" && f.min != null && f.max != null) {
            const val = filters[f.key] ?? f.max;
            return (
              <div key={f.key} data-testid={`filter-${f.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B]">{f.name}</label>
                  <span className="text-[11px] font-mono-data text-[#0055FF] font-semibold">{val === f.max ? "Any" : val}</span>
                </div>
                <Slider
                  min={f.min}
                  max={f.max}
                  step={f.step || 1}
                  value={[val]}
                  onValueChange={(v) => update(f.key, v[0])}
                  data-testid={`custom-${f.key}-slider`}
                />
              </div>
            );
          }
          return null;
        })}
    </aside>
  );
}


/**
 * v2.38 — BudgetSelect
 * Premium-styled native <select> wrapper. Native picker = best mobile UX
 * (system wheel picker on iOS, scrollable list on Android), but we hide the
 * default chrome and render our own pill with focus glow + chevron icon.
 */
function BudgetSelect({ label, value, options, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-zinc-500 block mb-1.5">{label}</span>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full min-h-[48px] pl-4 pr-10 text-[14px] font-bold text-zinc-900 bg-white border border-zinc-200 rounded-xl appearance-none cursor-pointer transition-all focus:outline-none focus:border-[#0055FF] focus:ring-4 focus:ring-[#0055FF]/15 hover:border-zinc-300"
          data-testid={testid}
          aria-label={label}
        >
          {options.map((o) => (
            <option key={o.v} value={o.v}>{o.l}</option>
          ))}
        </select>
        <svg
          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none transition-transform group-hover:text-[#0055FF]"
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
    </label>
  );
}
